<template>
  <div class="wiki-page">
    <!-- ========== 顶部导航 ========== -->
    <AppHeader />

    <!-- ========== 页面头部 ========== -->
    <div class="page-header">
      <div class="header-inner">
        <div class="header-left">
          <div class="header-badge">
            <span class="badge-dot"></span>
            知识库
          </div>
          <h1>产品 Wiki</h1>
          <p>按产品分类浏览文档，快速定位对应产品资料</p>
        </div>
      </div>
    </div>

    <!-- ========== 主体区域：搜索 + 左筛选 + 右展示 ========== -->
    <section class="main-section">
      <div class="main-inner">
        <!-- 搜索栏 -->
        <div class="search-bar">
          <el-input v-model="searchKeyword" placeholder="搜索产品型号、名称、关键词..." size="large" clearable
            @keyup.enter="handleSearch" @clear="handleSearch" class="search-input">
            <template #prefix>
              <el-icon>
                <Search />
              </el-icon>
            </template>
          </el-input>
          <span class="search-result">
            共 <strong>{{ totalCount }}</strong> 个文档
            <span v-if="selectedCategoryLabel !== '全部产品'">（当前分类：{{ selectedCategoryLabel }}）</span>
          </span>
        </div>

        <div class="content-wrapper">
          <!-- ====== 左侧筛选区（树形分类） ====== -->
          <aside class="filter-sidebar">
            <div class="filter-header">
              <h4>文档分类</h4>
              <el-button type="text" size="small" @click="resetFilters" class="reset-btn">
                <el-icon>
                  <Refresh />
                </el-icon> 重置
              </el-button>
            </div>

          
            <!-- 循环大类 -->
            <div v-for="cat in categoryTree" :key="cat.id" class="filter-group">
              <button class="filter-group-header" :class="{ active: selectedCategory?.type === 'parent' && selectedCategory.id === cat.id }" @click="handleParentClick(cat)">
                <span class="filter-label">{{ cat.name }}</span>
                <span class="filter-arrow" :class="{ expanded: expandedParents.has(cat.id) }">▾</span>
              </button>
              <div v-if="expandedParents.has(cat.id)" class="filter-children">
                <button v-for="child in cat.children" :key="child.id" class="filter-child"
                  :class="{ active: selectedCategory && selectedCategory.type === 'child' && selectedCategory.id === child.id }"
                  @click.stop="selectCategory('child', child)">
                  {{ child.name }}
                </button>
              </div>
            </div>

            
          </aside>

          <!-- ====== 右侧 Wiki 列表 ====== -->
          <div class="wiki-content">
            <!-- 加载状态 -->
            <div v-if="loading" class="wiki-grid skeleton-grid">
              <div v-for="i in 6" :key="i" class="wiki-card-skeleton">
                <div class="skeleton-image"></div>
                <div class="skeleton-text"></div>
                <div class="skeleton-text short"></div>
                <div class="skeleton-tags"></div>
              </div>
            </div>

            <!-- Wiki 网格 -->
            <div v-else-if="list.length > 0" class="wiki-grid">
              <div v-for="item in list" :key="item.id" class="wiki-card" @click="goToWikiDetail(item.id)">
                <!-- 左侧图片 -->
                <div class="card-image-wrap">
                  <img v-if="item.image" :src="item.image" :alt="item.name" class="card-image" loading="lazy" />
                  <div v-else class="card-image-placeholder">
                    <span>{{ item.name?.slice(0, 1) || 'W' }}</span>
                  </div>
                </div>

                <!-- 右侧内容 -->
                <div class="card-body">
                  <div class="card-header">
                    <h3 class="card-title">{{ item.name }}</h3>
                    <span class="card-model">{{ item.model }}</span>
                  </div>
                  <p class="card-desc">{{ item.description || '' }}</p>
                  <div class="card-footer">
                    <span class="card-updated">{{ item.subcategoryName || item.categoryName || item.category || '' }}</span>
                    <button class="card-action" @click.stop="goToWikiDetail(item.id)">
                      获取文档 <el-icon>
                        <ArrowRight />
                      </el-icon>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 空状态 -->
            <div v-else class="empty-state">
              <el-icon :size="64">
                <Document />
              </el-icon>
              <h3>未找到匹配的文档</h3>
              <p>请尝试调整筛选条件或关键词</p>
              <el-button type="primary" plain @click="resetFilters">重置筛选</el-button>
            </div>

            <!-- 加载更多 -->
            <div class="load-more-wrap" v-if="hasMore && !loading && list.length > 0">
              <button class="load-more-btn" @click="loadMore" :disabled="loadingMore">
                <span v-if="!loadingMore">加载更多</span>
                <span v-else><el-icon class="spin">
                    <Loading />
                  </el-icon> 加载中</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Search, Refresh, Loading, ArrowRight, Document } from '@element-plus/icons-vue'
import AppHeader from '@/components/AppHeader.vue'
import { getWikiPage } from '@/api/wiki'
import { getCategories } from '@/api/product'

const router = useRouter()

// ========== 状态 ==========
const searchKeyword = ref('')
const categoryTree = ref([])
const allCategories = ref([])
const selectedCategory = ref(null)
const list = ref([])
const loading = ref(false)
const loadingMore = ref(false)
const hasMore = ref(false)
const currentPage = ref(1)
const pageSize = 12
const totalCount = ref(0)
const expandedParents = ref(new Set())

const selectedCategoryLabel = computed(() => {
  if (!selectedCategory.value) return '全部产品'
  if (selectedCategory.value.type === 'parent') {
    return selectedCategory.value.name
  }
  return selectedCategory.value.parentName ? `${selectedCategory.value.parentName} / ${selectedCategory.value.name}` : selectedCategory.value.name
})

const isRootCategory = (category) => {
  return category.parentId === null || category.parentId === undefined || category.parentId === 0
}

const findCategoryById = (id) => {
  if (!id && id !== 0) return null
  return allCategories.value.find(item => Number(item.id) === Number(id)) || null
}

const getCategoryNameById = (id) => {
  return findCategoryById(id)?.name || ''
}

const buildCategoryTree = (categories) => {
  return categories
    .filter(isRootCategory)
    .map((parent) => ({
      id: parent.id,
      name: parent.name,
      children: categories
        .filter((child) => Number(child.parentId) === Number(parent.id))
        .map((child) => ({
          id: child.id,
          name: child.name,
          parentId: child.parentId
        }))
    }))
}

const selectCategory = (type, category) => {
  if (!type) {
    selectedCategory.value = null
    expandedParents.value = new Set()
    loadWikis({ page: 1, keyword: searchKeyword.value, categoryId: null, subcategoryId: null })
    return
  }

  if (type === 'parent') {
    selectedCategory.value = { type, id: category.id, name: category.name }
    expandedParents.value = new Set([category.id])
    loadWikis({ page: 1, keyword: searchKeyword.value, categoryId: category.id, subcategoryId: null })
  } else if (type === 'child') {
    const parentId = category.parentId
    const parentCategory = categoryTree.value.find(item => Number(item.id) === Number(parentId))
    selectedCategory.value = {
      type,
      id: category.id,
      name: category.name,
      parentId: parentId,
      parentName: parentCategory?.name || ''
    }
    expandedParents.value = new Set([parentId].filter(Boolean))
    loadWikis({ page: 1, keyword: searchKeyword.value, categoryId: parentId, subcategoryId: category.id })
  }
}

const handleParentClick = (category) => {
  expandedParents.value = new Set([category.id])
  selectCategory('parent', category)
}

// 搜索时保留当前分类筛选
const handleSearch = () => {
  const sel = selectedCategory.value
  if (sel) {
    if (sel.type === 'parent') {
      loadWikis({ page: 1, keyword: searchKeyword.value, categoryId: sel.id, subcategoryId: null })
    } else if (sel.type === 'child') {
      loadWikis({ page: 1, keyword: searchKeyword.value, categoryId: null, subcategoryId: sel.id })
    }
  } else {
    loadWikis({ page: 1, keyword: searchKeyword.value, categoryId: null, subcategoryId: null })
  }
}

// 重置
const resetFilters = () => {
  searchKeyword.value = ''
  selectedCategory.value = null
  expandedParents.value = new Set()
  loadWikis({ page: 1, keyword: '', categoryId: null, subcategoryId: null })
  ElMessage.success('已重置筛选条件')
}

// 加载更多时同样保留筛选
const loadMore = () => {
  if (loadingMore.value || !hasMore.value) return
  const sel = selectedCategory.value
  const extra = sel?.type === 'parent'
    ? { categoryId: sel.id, subcategoryId: null }
    : sel?.type === 'child'
      ? { categoryId: sel.parentId || null, subcategoryId: sel.id }
      : { categoryId: null, subcategoryId: null }

  loadWikis({
    page: currentPage.value + 1,
    append: true,
    keyword: searchKeyword.value,
    ...extra
  })
}

// ========== 加载分类数据 ==========
const loadCategories = async () => {
  try {
    const res = await getCategories()
    const categories = Array.isArray(res.data) ? res.data : (res.data?.list || [])
    allCategories.value = categories.filter(Boolean)
    categoryTree.value = buildCategoryTree(allCategories.value)
  } catch (error) {
    console.error('加载分类数据失败:', error)
    allCategories.value = []
    categoryTree.value = []
  }
}

// ========== 加载 Wiki 列表 ==========
const loadWikis = async ({ page = 1, append = false, keyword = '', categoryId = null, subcategoryId = null } = {}) => {
  if (append) {
    loadingMore.value = true
  } else {
    loading.value = true
    list.value = []
  }

  try {
    const params = {
      page,
      size: pageSize,
      keyword: keyword.trim()
    }

    if (categoryId != null) {
      params.categoryId = categoryId
    }
    if (subcategoryId != null) {
      params.subcategoryId = subcategoryId
    }

    const res = await getWikiPage(params)
    const data = res.data || {}
    const rawItems = Array.isArray(data) ? data : (data.list || [])
    const items = rawItems.map((item) => ({
      ...item,
      categoryName: findCategoryById(item.categoryId)?.name || '',
      subcategoryName: findCategoryById(item.subcategoryId)?.name || ''
    }))
    const total = data.total || data.totalCount || rawItems.length || 0
    totalCount.value = total
    hasMore.value = page * pageSize < total
    currentPage.value = page

    if (append) {
      list.value = [...list.value, ...items]
    } else {
      list.value = items
    }
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

// ========== 跳转到 Wiki 详情 ==========
const goToWikiDetail = (id) => {
  if (id) router.push(`/wiki/${id}`)
}

// ========== 生命周期 ==========
onMounted(async () => {
  await loadCategories()
  await loadWikis({ page: 1 })
})
</script>

<style scoped>
/* ========== 页面基础 ========== */
.wiki-page {
  padding-top: 72px;
  background: #f5f7fb;
  min-height: 100vh;
  font-family: -apple-system, 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

/* ========== 页面头部 ========== */
.page-header {
  background: #ffffff;
  border-bottom: 1px solid #eef2f6;
  padding: 24px 0;
}

.header-inner {
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 40px;
}

.header-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #4F46E5;
  background: rgba(79, 70, 229, 0.08);
  padding: 4px 16px 4px 10px;
  border-radius: 100px;
  margin-bottom: 8px;
}

.badge-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4F46E5;
  animation: pulse-dot 2s ease-in-out infinite;
}

@keyframes pulse-dot {

  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.4;
    transform: scale(0.8);
  }
}

.header-left h1 {
  font-size: 28px;
  font-weight: 700;
  color: #0a0a1a;
  margin: 0 0 4px 0;
  letter-spacing: -0.5px;
}

.header-left p {
  font-size: 14px;
  color: #8b95a9;
  margin: 0;
}

/* ========== 主体区域 ========== */
.main-section {
  max-width: 85%;
  margin: 0 auto;
  padding: 20px 40px 40px;
}

.main-inner {
  background: #ffffff;
  border-radius: 24px;
  padding: 28px 32px 32px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
}

/* ---- 搜索栏 ---- */
.search-bar {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f0f2f5;
}

.search-input {
  flex: 1;
}

.search-input :deep(.el-input__wrapper) {
  border-radius: 14px;
  padding: 6px 20px;
  background: #f5f7fb;
  border: 1px solid transparent;
  transition: all 0.3s ease;
}

.search-input :deep(.el-input__wrapper:hover) {
  border-color: #d1d5db;
  background: #fff;
}

.search-input :deep(.el-input__wrapper.is-focus) {
  border-color: #4F46E5;
  box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.08);
  background: #fff;
}

.search-result {
  font-size: 15px;
  color: #6b7280;
  white-space: nowrap;
}

.search-result strong {
  color: #0a0a1a;
  font-weight: 700;
}

/* ---- 内容包装器 ---- */
.content-wrapper {
  display: flex;
  gap: 36px;
  align-items: flex-start;
}

/* ====== 左侧筛选栏 ====== */
.filter-sidebar {
  flex: 0 0 240px;
  position: sticky;
  top: 92px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  padding-right: 8px;
}

.filter-sidebar::-webkit-scrollbar {
  width: 4px;
}

.filter-sidebar::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 4px;
}

.filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.filter-header h4 {
  font-size: 17px;
  font-weight: 600;
  color: #0a0a1a;
  margin: 0;
}

.reset-btn {
  color: #6b7280 !important;
  font-size: 13px;
  padding: 0 !important;
}

.reset-btn:hover {
  color: #4F46E5 !important;
}

.filter-group {
  margin-bottom: 12px;
}

.filter-group-header {
  width: 100%;
  border: none;
  background: transparent;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  color: #0a0a1a;
}

.filter-group-header:hover {
  background: #f3f4f6;
}

.filter-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.filter-arrow {
  font-size: 12px;
  color: #9ca3af;
  transition: transform 0.2s ease;
}

.filter-arrow.expanded {
  transform: rotate(180deg);
}

.filter-children {
  padding-left: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 6px;
}

.filter-child {
  border: none;
  background: #fff;
  padding: 8px 10px;
  text-align: left;
  border-radius: 8px;
  cursor: pointer;
  color: #6b7280;
  font-size: 13px;
  font-family: inherit;
  transition: all 0.2s ease;
}

.filter-child:hover {
  background: #f3f4f6;
  color: #1a1a2e;
}

.filter-child.active {
  background: rgba(79, 70, 229, 0.08);
  color: #4F46E5;
  font-weight: 500;
}

/* ====== 右侧产品列表 ====== */
.wiki-content {
  flex: 1;
  min-width: 0;
}

.wiki-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  /* 固定3列，每列等宽 */
  gap: 20px;
}

/* ---- 骨架屏 ---- */
.wiki-card-skeleton {
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  border: 1px solid #f0f2f5;
  display: flex;
  gap: 16px;
}

.skeleton-image {
  flex: 0 0 140px;
  height: 140px;
  border-radius: 12px;
  background: #eef2f6;
  animation: shimmer 1.5s ease-in-out infinite;
}

.skeleton-text {
  height: 14px;
  background: #eef2f6;
  border-radius: 4px;
  animation: shimmer 1.5s ease-in-out infinite;
  margin-bottom: 8px;
  flex: 1;
}

.skeleton-text.short {
  width: 60%;
}

.skeleton-tags {
  height: 24px;
  background: #eef2f6;
  border-radius: 4px;
  width: 40%;
  animation: shimmer 1.5s ease-in-out infinite;
  margin-top: 10px;
}

@keyframes shimmer {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }

  100% {
    opacity: 1;
  }
}

/* ---- 产品卡片（左图右文） ---- */
.wiki-card {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 16px;
  padding: 16px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  transition: all 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
}

.wiki-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 48px rgba(10, 10, 26, 0.08);
  border-color: rgba(79, 70, 229, 0.15);
}

/* 图片容器固定尺寸 */
.card-image-wrap {
  flex: 0 0 140px;
  width: 140px;
  height: 140px;
  border-radius: 12px;
  overflow: hidden;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.card-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eef2f6;
  color: #4F46E5;
  font-size: 32px;
  font-weight: 600;
}

/* 右侧内容 */
.card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

/* 标题最多两行 */
.card-title {
  font-size: 15px;
  font-weight: 600;
  color: #0a0a1a;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  max-width: 100%;
}

.card-model {
  font-size: 12px;
  font-weight: 500;
  color: #4F46E5;
  background: rgba(79, 70, 229, 0.08);
  padding: 0 10px;
  border-radius: 100px;
  white-space: nowrap;
  flex-shrink: 0;
}

.card-desc {
  font-size: 13px;
  color: #6b7280;
  margin: 4px 0 10px 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex-shrink: 0;
}

.card-specs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.spec-tag {
  font-size: 11px;
  font-weight: 500;
  color: #4b5563;
  background: #f3f4f6;
  padding: 2px 10px;
  border-radius: 100px;
  border: 1px solid #e5e7eb;
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #f0f2f5;
  padding-top: 12px;
  margin-top: auto;
  flex-shrink: 0;
}

.card-updated {
  font-size: 12px;
  color: #9ca3af;
}

.card-action {
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: none;
  color: #4F46E5;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  font-family: inherit;
}

.card-action:hover {
  background: rgba(79, 70, 229, 0.08);
}

/* ---- 空状态 ---- */
.empty-state {
  text-align: center;
  padding: 60px 24px;
  background: #fafbfc;
  border-radius: 16px;
  border: 1px solid #f0f2f5;
}

.empty-state .el-icon {
  color: #d1d5db;
  margin-bottom: 16px;
}

.empty-state h3 {
  font-size: 19px;
  color: #1a1a2e;
  margin: 0 0 8px 0;
}

.empty-state p {
  color: #9ca3af;
  margin: 0 0 20px 0;
  font-size: 15px;
}

/* ---- 加载更多 ---- */
.load-more-wrap {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}

.load-more-btn {
  padding: 10px 44px;
  border-radius: 100px;
  border: 1.5px solid #e5e7eb;
  background: transparent;
  color: #1a1a2e;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 8px;
}

.load-more-btn:hover:not(:disabled) {
  border-color: #0a0a1a;
  background: rgba(10, 10, 26, 0.04);
}

.load-more-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ========== 响应式 ========== */
@media (max-width: 1200px) {
  .filter-sidebar {
    flex: 0 0 200px;
  }

  .wiki-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
}

@media (max-width: 1024px) {
  .main-section {
    padding: 16px 24px 32px;
  }

  .main-inner {
    padding: 20px 20px 24px;
  }

  .card-image-wrap {
    flex: 0 0 120px;
    width: 120px;
    height: 120px;
  }
}

@media (max-width: 820px) {
  .main-section {
    padding: 12px 16px 24px;
  }

  .main-inner {
    padding: 16px 14px 20px;
  }

  .search-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .search-result {
    text-align: right;
    font-size: 14px;
  }

  .content-wrapper {
    flex-direction: column;
    gap: 24px;
  }

  .filter-sidebar {
    flex: none;
    width: 100%;
    position: static;
    max-height: none;
    overflow-y: visible;
    padding-right: 0;
  }

  .wiki-grid {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  }

  .page-header {
    padding: 16px 0;
  }

  .header-inner {
    padding: 0 24px;
  }
}

@media (max-width: 600px) {
  .wiki-card {
    flex-direction: column;
    padding: 16px;
  }

  .card-image-wrap {
    flex: 0 0 auto;
    width: 100%;
    height: 160px;
  }

  .card-body {
    width: 100%;
    justify-content: flex-start;
  }

  .card-footer {
    margin-top: 8px;
  }

  .wiki-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .main-section {
    padding: 8px 12px 16px;
  }

  .main-inner {
    padding: 12px 10px 16px;
    border-radius: 16px;
  }

  .header-inner {
    padding: 0 16px;
  }

  .header-left h1 {
    font-size: 22px;
  }

  .card-image-wrap {
    height: 140px;
  }

  .card-title {
    font-size: 14px;
  }

  .card-desc {
    font-size: 12px;
    -webkit-line-clamp: 1;
  }
}
</style>